"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import type { CatalogAbility, MarketplaceAbility } from "@bash/shared";
import { prefetchAbility } from "@/lib/abilities/prefetch";
import { firstGlyph, isIconUrl } from "@/lib/abilities/icon";
import { VestraLogoMark } from "@/components/ui/VestraLogoMark";
import { pickBento, pickColor } from "./v2/bento";
import { TabSkeleton } from "./v2/cards";
import { useAuth } from "@/contexts/AuthContext";
import { MarketplaceZapIcon } from "@/components/icons/MarketplaceZapIcon";
import { BotIcon } from "@/components/icons/BotIcon";
import { MarketplacePlugIcon } from "@/components/icons/MarketplacePlugIcon";
import { ShieldIcon } from "@/components/icons/ShieldIcon";
import { isAbilitiesSlimV1Enabled } from "@/lib/abilities/flags";

// ---------------------------------------------------------------------------
// Banner-DNA card: same recipe as FeaturedSlideCard at card scale.
//
// Bento gradient base + ambient corner glow keyed to the brand colour +
// 108px badge with inset highlight and brand-coloured drop shadow +
// editorial title / description / inventory chips / author byline /
// CTA pinned to the bottom-right corner. Wide aspect (flex row), badge
// top-aligned on the right with a touch of breathing room from the
// eyebrow, CTA absolutely positioned so its right edge aligns with the
// badge's right edge.
//
// Byline rendering: VestraLogoMark for author.id='vestra-official',
// <img src={author.logoUrl}> when an author logo URL is present (e.g.
// Composio), otherwise a small letter tile. No verified shield anywhere.
// ---------------------------------------------------------------------------

function formatInstalls(n: number): string {
  if (n >= 1000) {
    return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, "")}k`;
  }
  return n.toLocaleString();
}

type CardState = CatalogAbility["state"];

// (firstGlyph and isIconUrl now live in @/lib/abilities/icon — single source
// of truth across InstallConfirmModal, UninstallConfirmModal, AddToAbilityPicker,
// AbilityRecommendationCard, AbilityRow, and this file. Imported above.)

// Luminance from a #rrggbb hex (0..1). Falls back to a mid value for shapes
// we don't recognise so the default branch stays subtle.
function hexLuma(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return 0.5;
  const r = parseInt(m[1].slice(0, 2), 16) / 255;
  const g = parseInt(m[1].slice(2, 4), 16) / 255;
  const b = parseInt(m[1].slice(4, 6), 16) / 255;
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

// Derive a soft bento wash from the brand colour. Both luma extremes need a
// bump for opposite reasons: light brand colours (cream, off-white) have too
// little contrast against the elevated bg at 3%, and near-black colours have
// no hue for the eye to catch — at 3% they dissolve into a neutral haze that
// can read as a perceived green/cool tint next to colourful neighbours.
// Mid-range saturated colours stay deliberately faint so they whisper.
function bentoFromColor(color: string): string {
  const luma = hexLuma(color);
  const strong = luma < 0.15 ? 8 : luma > 0.7 ? 10 : 3;
  const weak = luma < 0.15 ? 3 : luma > 0.7 ? 4 : 1;
  return `linear-gradient(135deg, color-mix(in srgb, ${color} ${strong}%, transparent), color-mix(in srgb, ${color} ${weak}%, transparent))`;
}

// ---------------------------------------------------------------------------
// State pill (eyebrow row, replaces the corner badge). For comingSoon rows
// it renders as an amber "Coming soon" pill instead — same visual recipe as
// the featured-banner pill.
// ---------------------------------------------------------------------------

function StatePill({
  state,
  comingSoon,
  updateAvailable,
}: {
  state: CardState;
  comingSoon?: boolean;
  /**
   * True only when state==='installed' AND the source ability's version
   * has moved past the user's pinned baseline. Swaps the green "Installed"
   * pill for an amber "Update" one so the card pops in the grid.
   */
  updateAvailable?: boolean;
}) {
  if (comingSoon) {
    return (
      <span className="text-2.5 tracking-[0.12em] uppercase font-bold px-2 py-1 rounded-full bg-palette-amber/14 text-palette-amber whitespace-nowrap">
        Coming soon
      </span>
    );
  }
  if (state === "available") return null;
  // Update badge takes priority over the regular installed pill — same
  // visual slot, amber palette, "↑ Update" copy so it reads as actionable.
  if (updateAvailable) {
    return (
      <span className="text-2.5 tracking-[0.12em] uppercase font-bold px-2 py-1 rounded-full bg-palette-amber/18 text-palette-amber whitespace-nowrap">
        ↑ Update
      </span>
    );
  }
  const label =
    state === "system"
      ? "Active"
      : state === "personal"
        ? "Active"
        : "✓ Installed";
  return (
    <span className="text-2.5 tracking-[0.12em] uppercase font-bold px-2 py-1 rounded-full bg-palette-green/14 text-marketplace-grid-palette-green-tint whitespace-nowrap">
      {label}
    </span>
  );
}

function InventoryChip({
  icon,
  count,
  label,
  iconColor,
}: {
  icon: React.ReactNode;
  count: number;
  label: string;
  iconColor: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-bg-base/45 border border-border-subtle text-3 font-medium text-fg-default tabular-nums whitespace-nowrap">
      <span
        style={{ color: iconColor }}
        className="grid place-items-center opacity-90 shrink-0"
      >
        {icon}
      </span>
      <span>{count}</span>
      <span className="text-fg-subtle">{label}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// MarketplaceCard — banner-DNA wide card. Same data interface as before.
// ---------------------------------------------------------------------------

export interface MarketplaceCardProps {
  ability: CatalogAbility | MarketplaceAbility;
  /**
   * Called when the user clicks the inline "Install" CTA on an `available`
   * card. Parent owns the InstallConfirmModal so a single modal instance
   * services the whole grid. Omitting this prop hides the CTA entirely
   * (back-compat for callers passing plain `MarketplaceAbility` rows).
   */
  onInstall?: (ability: CatalogAbility) => void;
  /**
   * Called when the user clicks the inline "Uninstall" CTA on an `installed`
   * card. Mirror of `onInstall`: the parent owns the UninstallConfirmModal so
   * one instance services the whole grid. Without it, installed cards fall
   * back to the bare installs-count label (back-compat). Only `installed`
   * rows expose the action — `system`/`personal` (pinned defaults) never do.
   */
  onUninstall?: (ability: CatalogAbility) => void;
}

export function MarketplaceCard({
  ability,
  onInstall,
  onUninstall,
}: MarketplaceCardProps) {
  const { user } = useAuth();
  const userName =
    user?.user_metadata?.full_name ?? user?.user_metadata?.name ?? "You";
  const userAvatar: string | undefined =
    user?.user_metadata?.avatar_url ?? user?.user_metadata?.picture;

  const state: CardState =
    (ability as CatalogAbility).state ??
    (ability.installed_by_me ? "installed" : "available");

  const color = pickColor(ability.slug, ability.color);
  // Brand-coloured cards (ability.color set in the manifest) derive their
  // bento from that colour so the wash matches the brand; everything else
  // falls back to the hash-mapped pickBento for visual variety.
  const bento = ability.color
    ? bentoFromColor(ability.color)
    : pickBento(ability.slug);
  const iconAsUrl = isIconUrl(ability.icon);
  const badgeText = iconAsUrl
    ? ""
    : ability.icon || firstGlyph(ability.name) || "·";
  const authorInitial = firstGlyph(ability.author.name).toUpperCase();
  const comingSoon = !!ability.comingSoon;
  const showInstall = !comingSoon && state === "available" && !!onInstall;
  // Uninstall is offered only for marketplace abilities the caller actually
  // installed (state==='installed'). `system`/`personal` rows are pinned
  // defaults / user-authored and have no uninstall path, so they fall through
  // to the installs-count label like before.
  const showUninstall = !comingSoon && state === "installed" && !!onUninstall;
  const isVestraAuthor = ability.author.id === "vestra-official";
  const authorLogoUrl = ability.author.logoUrl;

  // Shared className + style so the comingSoon (div) and live (Link)
  // branches stay visually identical. comingSoon drops the prefetch +
  // hover-translate since there's no detail page to navigate to.
  const sharedClassName = [
    "group relative isolate overflow-hidden rounded-20",
    "border border-border-subtle bg-bg-elevated",
    "flex flex-row gap-5 p-5 sm:p-6 min-h-[252px]",
    "transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
    comingSoon
      ? "cursor-default"
      : "hover:border-border-muted hover:-translate-y-[3px] hover:shadow-marketplace-grid",
  ].join(" ");
  const sharedStyle: React.CSSProperties = {
    backgroundImage: bento,
    boxShadow:
      "0 1px 2px rgba(0,0,0,0.04), 0 8px 24px -12px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.04)",
  };

  const cardBody = (
    <>
      {/* Ambient corner glow — same recipe as banner, behind everything.
          Opacity tuned down to a whisper so it co-exists with the lighter
          bento wash without competing. */}
      <span
        aria-hidden
        className="absolute -top-24 -right-24 w-80 h-80 rounded-full pointer-events-none opacity-[0.022]"
        style={{
          background: `radial-gradient(closest-side, ${color}, transparent 70%)`,
          filter: "blur(40px)",
          zIndex: -1,
        }}
      />

      {/* Body — left column */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Eyebrow row: category + state pill */}
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {ability.category && (
            <span className="text-2.5 tracking-[0.16em] uppercase font-semibold text-fg-subtle px-2 py-1 rounded-full border border-border-subtle bg-bg-base/40">
              {ability.category}
            </span>
          )}
          <StatePill
            state={state}
            comingSoon={comingSoon}
            updateAvailable={(ability as CatalogAbility).update_available}
          />
        </div>

        {/* Title */}
        <h3 className="text-5.5 font-semibold tracking-[-0.022em] leading-[1.1] text-fg-default text-balance">
          {ability.name}
        </h3>

        {/* Short description */}
        <p className="mt-2 text-3.5 leading-[1.55] text-fg-muted line-clamp-2 max-w-[50ch] text-pretty">
          {ability.description}
        </p>

        {/* Inventory chips */}
        <div className="mt-3.5 flex items-center gap-1.5 flex-wrap">
          <InventoryChip
            icon={<MarketplaceZapIcon />}
            count={ability.counts.skills}
            label="skills"
            iconColor="var(--color-palette-purple)"
          />
          <InventoryChip
            icon={<BotIcon />}
            count={ability.counts.agents}
            label="agents"
            iconColor="var(--color-palette-pink)"
          />
          {ability.slug === "my-kit" ? (
            <>
              <InventoryChip
                icon={<MarketplacePlugIcon />}
                count={ability.counts.mcps}
                label="MCPs"
                iconColor="var(--color-palette-amber)"
              />
              {/* hooks has no count source yet (counts is {skills,agents,mcps});
                  stays 0 until hooks are surfaced per-ability, matching the
                  "hooks coming soon" tab. Gone entirely under the slim flag,
                  where an ability is skills and connections. */}
              {!isAbilitiesSlimV1Enabled() && (
                <InventoryChip
                  icon={<ShieldIcon />}
                  count={0}
                  label="hooks"
                  iconColor="var(--color-palette-teal)"
                />
              )}
            </>
          ) : (
            <InventoryChip
              icon={<MarketplacePlugIcon />}
              count={ability.counts.mcps}
              label="MCPs"
              iconColor="var(--color-palette-amber)"
            />
          )}
        </div>

        {/* Author byline — pinned to bottom-left of body. Brand-aware:
            My Kit → current user's avatar + name (each user's My Kit is
            their own space), Vestra → bundled mark, anything with a
            logoUrl → that image, else a small colour-tinted letter tile.
            No verified shield. */}
        <div className="mt-auto pt-4 inline-flex items-center gap-1.5 text-3 tabular-nums min-w-0">
          <span className="text-fg-subtle">by</span>
          {ability.slug === "my-kit" ? (
            <>
              {userAvatar ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={userAvatar}
                  alt=""
                  aria-hidden
                  className="size-[14px] rounded-full object-cover shrink-0"
                />
              ) : (
                <span
                  aria-hidden
                  className="size-[14px] rounded-full grid place-items-center text-2.5 font-bold text-white shrink-0 bg-fg-default"
                >
                  {(userName[0] || "Y").toUpperCase()}
                </span>
              )}
              <span className="text-fg-default font-medium truncate">
                {userName}
              </span>
            </>
          ) : isVestraAuthor ? (
            <VestraLogoMark size={14} className="shrink-0 rounded-4" />
          ) : authorLogoUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={authorLogoUrl}
              alt=""
              aria-hidden
              className="size-[14px] rounded-4 object-cover shrink-0"
            />
          ) : (
            <span
              aria-hidden
              className="size-[14px] rounded-4 grid place-items-center text-2.5 font-bold text-white shrink-0"
              style={{ background: color }}
            >
              {authorInitial}
            </span>
          )}
          {ability.slug !== "my-kit" && (
            <span className="text-fg-default font-medium truncate">
              {ability.author.name}
            </span>
          )}
        </div>
      </div>

      {/* Hero — right column. Badge sits a touch lower than the eyebrow row
          (10px breathing room from the card top padding) and its right edge
          aligns with the CTA below at `right: 22px`. */}
      <div className="self-start shrink-0 relative mt-2.5">
        <div
          className="size-[108px] rounded-24 grid place-items-center text-white font-bold relative overflow-hidden tracking-[-0.04em] leading-none"
          style={{
            background: color,
            fontSize: 54,
            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.22), 0 16px 36px -14px ${color}, 0 6px 16px -6px rgba(0,0,0,0.40)`,
          }}
        >
          <span
            aria-hidden
            className="absolute inset-0 pointer-events-none bg-marketplace-grid-inline"
          />
          {iconAsUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={ability.icon!}
              alt=""
              aria-hidden
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <span className="relative">{badgeText}</span>
          )}
        </div>
      </div>

      {/* CTA — four modes:
          comingSoon → "Notify me →" (no onClick, visual-only matching the banner),
          showInstall → live Install button that opens the parent install modal,
          showUninstall → neutral Uninstall button that opens the parent
            uninstall modal (only on installed rows),
          else → bare installs-count label. All anchor to the card's
          bottom-right so the rhythm is preserved. */}
      {comingSoon ? (
        <button
          type="button"
          className="absolute bottom-5.5 right-5.5 z-[2] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-12 bg-fg-default text-bg-base text-3.5 font-semibold hover:opacity-90 transition-opacity"
        >
          Notify me
          <span className="text-3.5 leading-none">→</span>
        </button>
      ) : showInstall ? (
        <button
          type="button"
          data-testid="install-cta"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onInstall!(ability as CatalogAbility);
          }}
          className="absolute bottom-5.5 right-5.5 z-[2] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-12 bg-fg-default text-bg-base text-3.5 font-semibold hover:opacity-90 transition-opacity"
        >
          Install
          <span className="text-3.5 leading-none">→</span>
        </button>
      ) : showUninstall ? (
        <button
          type="button"
          data-testid="uninstall-cta"
          onClick={(e) => {
            // The card is a <Link>; without these the click would navigate to
            // the detail page instead of opening the uninstall modal.
            e.preventDefault();
            e.stopPropagation();
            onUninstall!(ability as CatalogAbility);
          }}
          className="absolute bottom-5.5 right-5.5 z-[2] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-12 border border-border-muted bg-bg-base text-fg-default text-3.5 font-medium hover:bg-bg-muted hover:border-border-default transition-colors"
        >
          Uninstall
        </button>
      ) : (
        <span className="absolute bottom-7 right-6 z-[2] text-3 text-fg-faint tabular-nums whitespace-nowrap">
          {formatInstalls(ability.installed_count)} installs
        </span>
      )}
    </>
  );

  // comingSoon rows render as a non-navigating div (no Link, no prefetch);
  // live rows keep the Link wrapper + prefetch handlers so the detail page
  // can preload on hover/focus.
  return comingSoon ? (
    <div
      data-testid="marketplace-card"
      data-coming-soon="true"
      className={sharedClassName}
      style={sharedStyle}
    >
      {cardBody}
    </div>
  ) : (
    <Link
      href={`/abilities/${ability.slug}`}
      data-testid="marketplace-card"
      onMouseEnter={() => prefetchAbility(ability.slug)}
      onFocus={() => prefetchAbility(ability.slug)}
      className={sharedClassName}
      style={sharedStyle}
    >
      {cardBody}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// MarketplaceGrid — 2-column grid of banner-DNA cards. Owns its own section
// header so the page can drop it in directly.
// ---------------------------------------------------------------------------

interface MarketplaceGridProps {
  abilities: Array<CatalogAbility | MarketplaceAbility>;
  /** Section title — e.g. "Most installed" (default) or "Results" when filtering. */
  title?: string;
  /** Footer-summary copy (defaults to install-sorted blurb). */
  summary?: string;
  /**
   * Fires when the user clicks a card's inline "Install" CTA on an
   * `available` row. Forwarded to each `<MarketplaceCard>`. Parent owns the
   * confirmation modal so the whole grid shares one instance.
   */
  onInstall?: (ability: CatalogAbility) => void;
  /**
   * Fires when the user clicks a card's inline "Uninstall" CTA on an
   * `installed` row. Forwarded to each `<MarketplaceCard>`. Parent owns the
   * confirmation modal so the whole grid shares one instance.
   */
  onUninstall?: (ability: CatalogAbility) => void;
}

export function MarketplaceGrid({
  abilities,
  title = "Most installed",
  summary,
  onInstall,
  onUninstall,
}: MarketplaceGridProps) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-4 pb-2 border-b border-border-subtle">
        <h2 className="text-4 font-semibold text-fg-default leading-none">
          {title}
        </h2>
        <span className="text-3 text-fg-muted">
          {summary ?? `${abilities.length} in catalog · sorted by installs`}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-4.5 max-[900px]:grid-cols-1">
        {abilities.map((ability) => (
          <MarketplaceCard
            key={ability.id}
            ability={ability}
            onInstall={onInstall}
            onUninstall={onUninstall}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeletons for /abilities.
//
// Two layers, on purpose:
//   - MarketplaceGridSkeleton — the *content* below the page chrome: featured
//     carousel + "Installed for Bash" section + "All abilities" grid. This is
//     what AbilitiesTab returns while its data is in-flight, since the page
//     chrome (header / tabs / search) is already rendered by page.tsx and
//     remounting it would create a duplicate stack.
//   - AbilitiesPageSkeleton — the full page shell (header + TabsBar + search
//     + MarketplaceGridSkeleton). Used by loading.tsx and page.tsx's Suspense
//     fallback for `useSearchParams()`, both of which fire before page.tsx
//     has rendered any chrome.
//
// Both mirror the real blueprint pixel-for-pixel so the swap doesn't shift
// layout. The featured carousel placeholder is min-h 440 to match the
// tallest live slide (Higgsfield, with the 320px hero image + padding).
// ---------------------------------------------------------------------------

const sk = { background: "var(--bg-muted)" };

function WideCardSkeleton({ index: i }: { index: number }) {
  return (
    <div
      className="relative flex flex-row gap-5 p-5.5 bg-bg-subtle border border-border-subtle rounded-20 min-h-63"
      style={{ opacity: 0.6 + i * 0.04 }}
    >
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex gap-1.5 mb-3">
          <div
            className="animate-pulse-subtle-1 w-[64px] h-[18px] rounded-full"
            style={{ ...sk }}
          />
          {i % 2 === 0 && (
            <div
              className="animate-pulse-subtle-1 w-[76px] h-[18px] rounded-full"
              style={{ ...sk }}
            />
          )}
        </div>
        <div
          className="animate-pulse-subtle-1 max-w-[220px] h-[24px] rounded-6"
          style={{
            ...sk,
            width: `${48 + (i % 3) * 14}%`,
          }}
        />
        <div
          className="animate-pulse-subtle-1 w-[100%] h-[12px] rounded-4 mt-2.5"
          style={{
            ...sk,
          }}
        />
        <div
          className="animate-pulse-subtle-1 h-[12px] rounded-4 mt-1.5"
          style={{
            ...sk,
            width: `${58 + (i % 4) * 8}%`,
          }}
        />
        <div className="flex gap-1.5 mt-3.5">
          {[80, 86, 74].map((w, idx) => (
            <div
              key={idx}
              className="animate-pulse-subtle-1 h-[22px] rounded-full"
              style={{ ...sk, width: w }}
            />
          ))}
        </div>
        <div className="mt-auto pt-4 flex items-center gap-1.5">
          <div
            className="animate-pulse-subtle-1 w-[14px] h-[14px] rounded-4"
            style={{ ...sk }}
          />
          <div
            className="animate-pulse-subtle-1 h-[12px] rounded-4"
            style={{
              ...sk,
              width: 88 + (i % 3) * 14,
            }}
          />
        </div>
      </div>
      <div className="shrink-0 self-start mt-2.5">
        <div
          className="animate-pulse-subtle-1 w-[108px] h-[108px] rounded-24"
          style={{ ...sk }}
        />
      </div>
      <div
        className="absolute animate-pulse-subtle-1 w-[80px] h-[30px] rounded-10 bottom-6 right-6"
        style={{
          ...sk,
        }}
      />
    </div>
  );
}

// Content-only skeleton — assumed mounted INSIDE the page chrome (so no
// header/tabs/search are drawn here). Renders the featured carousel +
// Installed for Bash section + All abilities grid.
export function MarketplaceGridSkeleton() {
  return (
    <>
      {/* Featured carousel banner */}
      <div className="relative bg-bg-elevated border border-border-subtle rounded-20 p-8 mb-10 overflow-hidden max-[820px]:p-7 min-h-[440px]">
        <div className="grid gap-12 items-center max-[820px]:gap-6 max-[820px]:grid-cols-1 grid-cols-[minmax(0,_1fr)_auto]">
          <div className="flex flex-col gap-4 max-w-160">
            {/* Eyebrow: star + "New" + Category + Coming soon */}
            <div className="flex items-center gap-2 flex-wrap">
              <div
                className="animate-pulse-subtle-1 w-[54px] h-[12px] rounded-4"
                style={{ ...sk }}
              />
              <div
                className="animate-pulse-subtle-1 w-[78px] h-[20px] rounded-full"
                style={{ ...sk }}
              />
              <div
                className="animate-pulse-subtle-1 w-[100px] h-[20px] rounded-full"
                style={{ ...sk }}
              />
            </div>
            {/* Title */}
            <div
              className="animate-pulse-subtle-1 w-[260px] h-[40px] rounded-6"
              style={{ ...sk }}
            />
            {/* Tagline */}
            <div
              className="animate-pulse-subtle-1 w-[340px] h-[22px] rounded-6 mt--2"
              style={{
                ...sk,
              }}
            />
            {/* Description (3 lines) */}
            <div className="flex flex-col gap-2">
              <div
                className="animate-pulse-subtle-1 w-[100%] h-[14px] rounded-4"
                style={{ ...sk }}
              />
              <div
                className="animate-pulse-subtle-1 w-[92%] h-[14px] rounded-4"
                style={{ ...sk }}
              />
              <div
                className="animate-pulse-subtle-1 w-[78%] h-[14px] rounded-4"
                style={{ ...sk }}
              />
            </div>
            {/* Try saying chips */}
            <div className="flex items-center gap-1.5 flex-wrap mt-1">
              <div
                className="animate-pulse-subtle-1 w-[78px] h-[13px] rounded-4"
                style={{ ...sk }}
              />
              {[148, 116, 132].map((w, idx) => (
                <div
                  key={idx}
                  className="animate-pulse-subtle-1 h-[24px] rounded-full"
                  style={{ ...sk, width: w }}
                />
              ))}
            </div>
            {/* Inventory chips */}
            <div className="flex items-center gap-1.5 flex-wrap mt-1">
              <div
                className="animate-pulse-subtle-1 w-[52px] h-[13px] rounded-4"
                style={{ ...sk }}
              />
              {[64, 66, 62, 62].map((w, idx) => (
                <div
                  key={idx}
                  className="animate-pulse-subtle-1 h-[22px] rounded-full"
                  style={{ ...sk, width: w }}
                />
              ))}
            </div>
            {/* CTA */}
            <div
              className="animate-pulse-subtle-1 w-[124px] h-[42px] rounded-12 mt-1"
              style={{
                ...sk,
              }}
            />
            {/* Author byline */}
            <div className="flex items-center gap-2">
              <div
                className="animate-pulse-subtle-1 w-[24px] h-[12px] rounded-4"
                style={{ ...sk }}
              />
              <div
                className="animate-pulse-subtle-1 w-[14px] h-[14px] rounded-4"
                style={{ ...sk }}
              />
              <div
                className="animate-pulse-subtle-1 w-[96px] h-[12px] rounded-4"
                style={{ ...sk }}
              />
            </div>
          </div>
          {/* Right: hero badge area (152×152 in real card, sometimes a wider hero image) */}
          <div className="flex items-center justify-center max-[820px]:order-2 max-[820px]:mx-auto">
            <div
              className="animate-pulse-subtle-1 w-[152px] h-[152px] rounded-28"
              style={{ ...sk }}
            />
          </div>
        </div>
        {/* Carousel dot navigators */}
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
          <div
            className="animate-pulse-subtle-1 w-[24px] h-[8px] rounded-full opacity-75"
            style={{
              ...sk,
            }}
          />
          <div
            className="animate-pulse-subtle-1 w-[8px] h-[8px] rounded-full opacity-45"
            style={{
              ...sk,
            }}
          />
          <div
            className="animate-pulse-subtle-1 w-[8px] h-[8px] rounded-full opacity-45"
            style={{
              ...sk,
            }}
          />
        </div>
      </div>

      {/* "Installed for Bash" — present for any logged-in user because
            Essentials is auto-installed (state='system'). Showing 2 tiles so
            the skeleton matches a typical post-onboarding shelf. */}
      <section className="mb-10">
        <div className="flex items-baseline justify-between mb-4 pb-2 border-b border-border-subtle">
          <div
            className="animate-pulse-subtle-1 w-[168px] h-[18px] rounded-4"
            style={{ ...sk }}
          />
          <div
            className="animate-pulse-subtle-1 w-[92px] h-[12px] rounded-4"
            style={{ ...sk }}
          />
        </div>
        <div className="grid grid-cols-2 gap-4.5 max-[900px]:grid-cols-1">
          {Array.from({ length: 2 }).map((_, i) => (
            <WideCardSkeleton key={i} index={i} />
          ))}
        </div>
      </section>

      {/* "All abilities" section — wide-card grid */}
      <div>
        <div className="flex items-baseline justify-between mb-4 pb-2 border-b border-border-subtle">
          <div
            className="animate-pulse-subtle-1 w-[140px] h-[18px] rounded-4"
            style={{ ...sk }}
          />
          <div
            className="animate-pulse-subtle-1 w-[160px] h-[12px] rounded-4"
            style={{ ...sk }}
          />
        </div>
        <div className="grid grid-cols-2 gap-4.5 max-[900px]:grid-cols-1">
          {Array.from({ length: 4 }).map((_, i) => (
            <WideCardSkeleton key={i + 2} index={i + 2} />
          ))}
        </div>
      </div>
    </>
  );
}

// The chrome half of a full-page skeleton — header + tabs + search — shared by
// both page skeletons below. `skills-page__header` / `__content` carry the
// dock-gap rules, so they stay exactly where they were.
function MarketplacePageChrome({ children }: { children: ReactNode }) {
  return (
    <>
      {/* Page header — title + subtitle + "New Ability" (no eyebrow). */}
      <div className="skills-page__header">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div
              className="animate-pulse-subtle-1 w-[192px] h-[36px] rounded-6 mb-2.5"
              style={{
                ...sk,
              }}
            />
            <div
              className="animate-pulse-subtle-1 w-[360px] h-[16px] rounded-4"
              style={{ ...sk }}
            />
          </div>
          <div
            className="animate-pulse-subtle-1 w-[140px] h-[38px] rounded-16"
            style={{ ...sk }}
          />
        </div>
      </div>

      <div className="skills-page__content flex-1">
        {/* TabsBar — segmented control with the three top-level tabs */}
        <div className="flex mb-3">
          <div className="inline-flex items-center gap-1 p-1 rounded-20 bg-bg-elevated border border-border-subtle shadow-marketplace-grid-inline">
            {[88, 62, 62].map((w, i) => (
              <div
                key={i}
                className="animate-pulse-subtle-1 h-[36px] rounded-12"
                style={{
                  ...sk,
                  width: w,
                  opacity: i === 0 ? 0.55 : 0.9,
                }}
              />
            ))}
          </div>
        </div>

        {/* Search bar — matches the real h-12 rounded-20 input */}
        <div
          className="animate-pulse-subtle-1 w-[100%] h-[48px] rounded-16 mb-7"
          style={{
            ...sk,
          }}
        />

        {children}
      </div>
    </>
  );
}

// Full-page skeleton — header + tabs + search + content. Used by the route's
// loading.tsx and by page.tsx's outer Suspense fallback (both of which fire
// before page.tsx has rendered its real chrome).
export function AbilitiesPageSkeleton() {
  return (
    <MarketplacePageChrome>
      <MarketplaceGridSkeleton />
    </MarketplacePageChrome>
  );
}

// The same chrome around the cream-gold tab skeleton. /abilities is the only
// route that lands on the new design, so it is the only one whose full-page
// skeleton may speak it — AbilitiesPageSkeleton above still serves skills,
// agents and the three detail pages, which all land on the older layout and
// would otherwise flash a catalog that never arrives.
export function AbilitiesTabPageSkeleton() {
  return (
    <MarketplacePageChrome>
      <TabSkeleton />
    </MarketplacePageChrome>
  );
}
